<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden">
    <template v-if="!isMobile">
      <div
        class="tw:flex tw:h-[48px] tw:items-center tw:justify-between tw:px-2"
      >
        <div>
          <InputText
            v-if="showQuery"
            v-model="query"
            clearable
            data-testid="query"
            new-design
            :placeholder="searchPlaceholder"
            rounded
          >
            <template #prepend>
              <BaseIcon
                class="tw:text-denim-600"
                icon="search"
              />
            </template>
          </InputText>
        </div>
        <div class="tw:flex tw:gap-2">
          <Tabs
            v-model="view as string"
            class="tw:gap-1 tw:font-bold"
            data-testid="tabs"
          >
            <div>
              <Tab
                data-testid="tab-results"
                :disabled="readOnly"
                indicator-color="blue"
                :label="
                  getLocaleText(
                    'modals.advanced_source_filters_modal.results_tab'
                  )
                "
                name="results"
                new-design
                type="pill"
              >
                <i18n-t
                  keypath="modals.advanced_source_filters_modal.all_results"
                  :plural="totalSources"
                  scope="global"
                  tag="span"
                >
                  <template #count>
                    {{ formattedTotalSources }}
                  </template>
                </i18n-t>
              </Tab>
              <StreemTooltip
                v-if="readOnly"
                anchor="top middle"
                self="bottom middle"
              >
                {{
                  getLocaleText(
                    "modals.advanced_source_filters_modal.results_tab_read_only"
                  )
                }}
              </StreemTooltip>
              <StreemTooltip
                v-if="totalSources >= SOURCES_LIMIT"
                anchor="top middle"
                self="bottom middle"
              >
                <i18n-t
                  keypath="modals.advanced_source_filters_modal.results_tab_limit"
                  scope="global"
                  tag="span"
                >
                  <template #newline>
                    <br />
                  </template>
                </i18n-t>
              </StreemTooltip>
            </div>
            <Tab
              v-if="selectedSources.length"
              data-testid="tab-selection"
              indicator-color="blue"
              :label="
                getLocaleText(
                  'modals.advanced_source_filters_modal.selected_tab'
                )
              "
              name="selected"
              new-design
              type="pill"
            >
              <i18n-t
                class="tw:font-bold"
                keypath="modals.advanced_source_filters_modal.selected_sources"
                :plural="selectedSources.length"
                scope="global"
                tag="span"
              >
                <template #count>
                  {{ selectedSources.length }}
                </template>
              </i18n-t>
            </Tab>
          </Tabs>
        </div>
      </div>
      <BaseSeparator />
    </template>
    <SourceList
      v-if="view === 'results'"
      v-model:selected-sources="selectedSources"
      v-model:total-sources="totalSources"
      data-testid="results"
      :editing-stream="editingStream"
      :query="sourcesListQuery"
      :stream="stream"
      @edit-stream="emit('editStream')"
    />
    <SourceSelection
      v-else
      v-model:selected-sources="selectedSources"
      data-testid="selection"
      :query="selectedSourcesQuery"
      :read-only="readOnly"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";

import { getLocaleNumber, getLocaleText } from "shared/boot/i18n";
import {
  BaseIcon,
  BaseSeparator,
  InputText,
  Tab,
  Tabs,
} from "shared/components/base";
import StreemTooltip from "shared/components/base/StreemTooltip.vue";
import SourceList from "shared/components/sources/SourceList.vue";
import SourceSelection from "shared/components/sources/SourceSelection.vue";
import useSourceFilters from "shared/composables/useSourceFilters";
import type { MediumName } from "shared/helpers/media";
import type { Source } from "shared/helpers/sourceFilters";
import type { Nullable, Stream } from "shared/types";

const SOURCES_LIMIT = 10000;

export type SourceListSelectionView = "results" | "selected";

const { totalFilters } = useSourceFilters();

const selectedSources = defineModel<Source[]>("selectedSources", {
  default: () => [],
});

const view = defineModel<SourceListSelectionView>("view", {
  default: "results",
});

interface SouceListSelectionProps {
  stream?: Nullable<Stream>;
  readOnly?: boolean;
  editingStream?: boolean;
  enabledMedia?: MediumName[];
}

const props = withDefaults(defineProps<SouceListSelectionProps>(), {
  stream: null,
  enabledMedia: () => [],
});

const emit = defineEmits<{
  close: [];
  selectedSources: [Source[]];
  editStream: [];
}>();

const isMobile = inject("isMobile");

const sourcesListQuery = ref("");
const selectedSourcesQuery = ref("");
const totalSources = ref(0);

const query = computed({
  get() {
    if (view.value === "results") {
      return sourcesListQuery.value;
    }

    return selectedSourcesQuery.value;
  },
  set(value) {
    if (view.value === "results") {
      sourcesListQuery.value = value;

      return;
    }

    selectedSourcesQuery.value = value;
  },
});

const showQuery = computed<boolean>(() => {
  if (view.value === "results") {
    return Boolean(totalFilters.value && !props.readOnly);
  }

  return Boolean(selectedSources.value.length);
});

const formattedTotalSources = computed<string>(() => {
  let localeNumber = getLocaleNumber(totalSources.value);

  if (totalSources.value >= SOURCES_LIMIT) {
    localeNumber += "+";
  }

  return localeNumber;
});

const searchPlaceholder = computed<string>(() => {
  if (view.value === "results") {
    return getLocaleText(
      "modals.advanced_source_filters_modal.search_results_placeholder"
    );
  }

  return getLocaleText(
    "modals.advanced_source_filters_modal.search_selection_placeholder"
  );
});

watch(totalFilters, () => {
  if (totalFilters) view.value = "results";
});

watch(selectedSources, () => {
  if (!selectedSources.value.length && !isMobile) {
    view.value = "results";
  }
});
</script>
